import { Component, DestroyRef, effect, inject, input, linkedSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { Cartographie } from '../../../../../cartographie/models/cartographie.model';
import { SidepanelService } from '../../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrErrorComponent } from '../../../../../shared/components/toastr/toastr-error.component';
import { ToastrSuccessComponent } from '../../../../../shared/components/toastr/toastr-success.component';
import { ProductionUpdateRequest } from '../../../../référentiel-métier-ressources-professionnelles/requests/production-update-request.model';
import { RéférentielMétierRessourcesProfessionnellesService } from '../../../../référentiel-métier-ressources-professionnelles/référentiel-métier-ressources-professionnelles.service';
import { ProductionStore } from '../../../../référentiel-métier-ressources-professionnelles/store/production.store';
import { Production } from '../../../processus-aptitude/models/production.model';
import { AptitudeStore } from '../../../processus-aptitude/store/aptitude.store';

@Component({
  selector: 'app-production-form',
  imports: [
    ReactiveFormsModule,
    HlmSelectImports,
    BrnSelectImports,
    HlmInputImports,
    LabelRequiredDirective,
    HlmFieldImports,
  ],
  templateUrl: './production-form.component.html',
})
export class ProductionFormComponent implements OnInit {
  readonly #aptitudeStore = inject(AptitudeStore);
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #productionStore = inject(ProductionStore);
  readonly #ressourceProfessionnelleService = inject(RéférentielMétierRessourcesProfessionnellesService);

  readonly cartographie = input.required<Cartographie>();
  readonly production = input.required<Production>();
  readonly idAptitude = input.required<string>();

  readonly aptitudes = linkedSignal(() => this.#aptitudeStore.entities());

  readonly form = this.#fb.nonNullable.group({
    nom: [null, [Validators.required]],
    aptitude: [null, [Validators.required]],
  });

  constructor() {
    effect(() => {
      const production = this.production();
      const idAptitude = this.idAptitude();

      this.nom.setValue(production.nom);
      this.aptitude.setValue(idAptitude);
    });
  }

  ngOnInit(): void {
    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  onSubmit() {
    if (this.nom.hasError('required')) {
      return;
    }

    const toastrId = uuidv7();

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      toast.custom(ToastrErrorComponent, {
        componentProps: {
          message: 'Vous devez remplir tous les champs pour pouvoir enregistrer.',
          titre: "Impossible d'enregistrer",
          id: toastrId,
        },
        id: toastrId,
      });
      return;
    }

    const data: ProductionUpdateRequest = {
      idAptitude: this.aptitude.value,
      nom: this.nom.value,
    };

    this.#productionStore.modifier({
      idProduction: this.production().id,
      idCartographie: this.cartographie().id,
      data,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Modification de la production réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#ressourceProfessionnelleService.refreshData.next(true);
        this.#productionStore.modifierProduction({
          production: {
            id: this.production().id,
            ressources: this.production().ressources,
            ...data,
          },
        });
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(data),
    });
  }

  alreadyExists(production: ProductionUpdateRequest) {
    const productions = this.aptitudes().flatMap((x) => x.productions!);

    const doublonsAvecProductionsExistants = productions.filter(
      (x) => production.nom === x.nom && production.idAptitude === x.idAptitude && this.production().id !== x.id,
    );

    if (doublonsAvecProductionsExistants.length > 0) {
      this.nom.setErrors({ notUnique: true });
    }
    this.form.markAllAsTouched();
  }

  get aptitude() {
    return this.form.get('aptitude') as AbstractControl;
  }

  get nom() {
    return this.form.get('nom') as AbstractControl;
  }
}
